import styled, { keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";
import { Card } from "../Title/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

const shimmer = keyframes`
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -50% 0;
  }
`;

/* Segment and specialty side by side on desktop, to quickly filter the list (stacked on phones) */
export const FilterCard = styled(Card)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  /* A disabled field's label fades with it */
  select:disabled + label {
    opacity: 0.3;
  }

  @media ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/* Color filter row, under the two fields: "all" + one swatch per base color */
export const ColorFilter = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  gap: 0.5rem;

  .swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 0.625rem;
    align-items: center;
  }

  button {
    flex-shrink: 0;
    transition: box-shadow 0.15s;
  }

  /* Ring around the chosen one, with the card's color in between */
  button[aria-pressed="true"] {
    box-shadow:
      0 0 0 2px var(--color-neutral-dark),
      0 0 0 4px var(--color-neutral-white);
  }

  .all {
    height: 28px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 100px;
  }

  .swatch {
    width: 28px;
    height: 28px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 100%;
  }
`;

/* The templates of the chosen field, as phone-shaped thumbnails */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;

  @media ${MOBILE} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }
`;

export const Thumb = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  text-align: left;

  .frame {
    position: relative;
    width: 100%;
    aspect-ratio: 9 / 16;
    overflow: hidden;
    background: var(--color-neutral-dark);
    border: 2px solid ${props => (props.$selected ? "var(--color-brand-blue)" : "var(--color-neutral-base)")};
    border-radius: 14px;
    /* The picked one also gets a ring, so it stands out on the dark background */
    box-shadow: ${props => (props.$selected ? "0 0 0 3px rgba(75, 16, 255, 0.45)" : "none")};
    transition:
      border-color 0.15s,
      box-shadow 0.15s;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: opacity 0.25s ease-out;
    }

    /* Image still loading: a light sheen sweeps the frame and the image stays hidden */
    &.loading {
      background: linear-gradient(100deg, transparent 30%, rgba(255, 255, 255, 0.06) 50%, transparent 70%)
        var(--color-neutral-dark);
      background-size: 200% 100%;
      animation: ${shimmer} 1.2s linear infinite;

      img {
        opacity: 0;
      }
    }
  }

  /* No image (or it failed): the template's name in the frame */
  .fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 1rem;
    font-family: Poppins, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-neutral-150);
    text-align: center;
  }

  .check {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 100%;
  }

  /* Three per row on phones: a smaller check */
  @media ${MOBILE} {
    gap: 0.375rem;

    .check {
      top: 6px;
      right: 6px;
      width: 20px;
      height: 20px;
    }
  }

  &:hover .frame {
    border-color: ${props => (props.$selected ? "var(--color-brand-blue)" : "var(--color-neutral-light)")};
  }

  &:focus-visible {
    outline: none;

    .frame {
      outline: 2px solid var(--color-brand-blue);
      outline-offset: 2px;
    }
  }
`;

/* While the list loads */
/* While the list loads: same sheen as a thumbnail whose image is loading */
export const ThumbSkeleton = styled.div`
  width: 100%;
  aspect-ratio: 9 / 16;
  background: linear-gradient(100deg, transparent 30%, rgba(255, 255, 255, 0.06) 50%, transparent 70%)
    var(--color-neutral-dark);
  background-size: 200% 100%;
  border: 2px solid var(--color-neutral-base);
  border-radius: 14px;
  animation: ${shimmer} 1.2s linear infinite;

  /* Phones show 3 per row: one row of them */
  @media ${MOBILE} {
    &:nth-child(n + 4) {
      display: none;
    }
  }
`;

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 20px;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  p {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }

  button {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

/* An error under the list (loading a template, applying it) */
export const ErrorNote = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  color: var(--color-feedback-danger);
`;
